import grid25Image from "@/assets/grid25.png";

export default function VoicepalChallengeGettingStarted() {
  return (
    <div className="max-w-4xl mx-auto prose prose-sm dark:prose-invert">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-primary mb-4">
          🚀 Getting Started with the Voicepal Creator Challenge
        </h1>
        <p className="text-lg text-primary/80">
          Welcome to the challenge! We're excited to have you onboard :)
        </p>
        <p className="text-base text-primary/70 mt-2">
          Whether you're a seasoned creator or trying this out for the first
          time, this guide will walk you through{" "}
          <strong>how to join, create, post, and get paid</strong>, step by
          step.
        </p>
      </div>

      <div className="relative aspect-video w-full mb-8">
        <iframe
          src="https://www.loom.com/embed/e04d70b621dd44b68a0e8472918e3e9c?sid=021b8a39-3e3e-4880-b4d8-3cc8d15f0de8"
          frameBorder="0"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        ></iframe>
      </div>

      <div className="border-t border-border pt-8">
        <h2 className="text-xl font-semibold text-primary mb-6 flex items-center gap-2">
          🧭 Step-by-Step: Your CreatorGrid Launch Checklist
        </h2>

        <div className="space-y-8">
          {/* Step 1 */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
              ✅ Step 1: Get to Know Voicepal
            </h3>
            <p className="text-primary/80 mb-4">
              Before you make content, explore the app. This is the best way to
              make something genuine and helpful.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-primary/80">
              <li>
                <a
                  href="https://go.sparkle.so/vp-challenge-brief"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline font-medium"
                >
                  Download Voicepal
                </a>{" "}
                on mobile or use the desktop version
              </li>
              <li>
                <strong>
                  Use the code:{" "}
                  <code className="bg-secondary px-2 py-1 rounded text-sm">
                    GRID25
                  </code>
                </strong>{" "}
                after logging in to get full access for free during the
                challenge.
                <img
                  src={grid25Image}
                  alt="Grid25 code redemption"
                  className="mt-2 rounded-lg border border-border"
                />
              </li>
              <li>
                Explore:
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    🎥{" "}
                    <a
                      href="https://www.youtube.com/watch?v=Us-DUuT-iVU"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-medium"
                    >
                      Ali Abdaal's app walkthrough
                    </a>
                  </li>
                  <li>
                    🧭{" "}
                    <a
                      href="https://www.notion.so/Voicepal-Creator-Playbook-Content-Direction-Guidelines-2321a88ee85c8050b359e4647e069864?pvs=21"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-medium"
                    >
                      The Creator Playbook
                    </a>{" "}
                    (with tips, walkthroughs, and examples)
                  </li>
                  <li>
                    🔍 The <strong>Get Inspired tab</strong> inside Voicepal to
                    see viral content from similar tools.
                    <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                      <li>
                        <strong>Similar App's Viral Content</strong> see
                        successful content made by other apps.
                      </li>
                      <li>
                        <strong>Other Creator Videos</strong> see what others
                        are making about Voicepal, filter by likes, views, or
                        date.
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ol>
          </div>

          {/* Step 2 */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
              ✅ Step 2: Make your 1st video to Join the Challenge
            </h3>
            <p className="text-primary/80 mb-4">
              You can post <strong>short-form or long-form</strong> content as
              TikToks, Reels, Shorts, or full YouTube videos.
            </p>
            <div className="mb-4">
              <h4 className="font-medium text-primary mb-2">
                Tips for success:
              </h4>
              <ul className="list-disc list-inside space-y-1 text-primary/80">
                <li>
                  <strong>Start with a relatable problem.</strong> A problem
                  that you, your friends and your audience have.
                </li>
                <li>
                  <strong>Show how the app helps</strong> (on mobile or desktop)
                </li>
                <li>
                  Add a <strong>clear CTA</strong> (e.g. "Download Voicepal!")
                </li>
                <li>
                  Use <strong>viral storytelling hooks</strong> (like
                  productivity hacks, journaling tips, etc.)
                </li>
                <li>
                  Be real, helpful, and relatable.{" "}
                  <strong>The more inspiring, the better!</strong>
                </li>
                <li>
                  <strong>Don't overthink it</strong>. You can always do more
                  content later, and every view counts!
                </li>
              </ul>
            </div>
            <p className="text-primary/80">
              That's it! Your video's performance (views, earnings, etc.) will
              now show up on the dashboard.
            </p>

            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="font-medium text-primary mb-2">
                🛠 Need help to get good ideas?
              </h4>
              <ul className="list-disc list-inside space-y-1 text-primary/80">
                <li>
                  Join a <strong>Daily Office Hours session</strong> with our
                  team (led by Ali's Head of Socials) to get hands-on help.
                  Spots are limited:{" "}
                  <a
                    href="https://calendly.com/melina-at-ali-abdaal/voicepal-creator-office-hours"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Book Here
                  </a>{" "}
                  or watch{" "}
                  <a
                    href="https://docs.google.com/document/d/1NtCQWSA6Wz-Dtb4a-hrLJUKCupRGaPEFHerDqRaTeLM/edit?tab=t.0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-medium"
                  >
                    previous recordings
                  </a>
                </li>
                <li>
                  <strong>
                    Keep an eye out for a daily email from Ali's Head of Socials
                    (via creators@creatorgrid.io)
                  </strong>
                  . She'll be sharing tips, ideas and best practices.
                </li>
                <li>
                  <strong>Check out the "Get Inspired" tab</strong> at the top
                  of this screen
                </li>
              </ul>
            </div>
          </div>

          {/* Step 3 */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
              ✅ Step 3: Connect Your Social Accounts and post your first video
            </h3>
            <p className="text-primary/80 mb-4">
              📌 Once your 1st video is ready to go:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-primary/80 mb-4">
              <li>
                Go to the top right corner and click your{" "}
                <strong>avatar</strong>.
              </li>
              <li>
                Click <strong>"Settings"</strong>.
              </li>
              <li>
                Hit the big red button: <strong>"Add Socials"</strong>.
              </li>
              <li>
                Connect up to one account per platform:
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    <strong>1x TikTok</strong>
                  </li>
                  <li>
                    <strong>1x Instagram</strong>
                  </li>
                  <li>
                    <strong>1x YouTube</strong>
                  </li>
                </ul>
              </li>
            </ol>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg mb-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                ⚠️ Only videos posted on these connected accounts will be
                tracked and eligible for payout.
              </p>
            </div>

            <ol
              start={5}
              className="list-decimal list-inside space-y-2 text-primary/80 mb-4"
            >
              <li>
                Once this is ready, go back to the{" "}
                <strong>main dashboard</strong> on CreatorGrid:
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    Click <strong>"+ Add New Post"</strong>
                  </li>
                  <li>
                    Paste the <strong>public link</strong> to your video
                  </li>
                </ul>
              </li>
            </ol>

            <p className="text-sm text-primary/60 italic mb-4">
              *It must match one of the connected accounts. Random links won't
              work.*
            </p>

            <p className="text-primary/80">You can:</p>
            <ul className="list-disc list-inside space-y-1 text-primary/80">
              <li>
                Use your <strong>existing personal accounts</strong>, or
              </li>
              <li>
                Create a <strong>new one</strong> for this challenge (just don't
                use "Voicepal" in the handle, names like{" "}
                <code className="bg-secondary px-1 py-0.5 rounded text-xs">
                  @voicedaily
                </code>{" "}
                or{" "}
                <code className="bg-secondary px-1 py-0.5 rounded text-xs">
                  @writeflow.ai
                </code>{" "}
                are totally fine!)
              </li>
            </ul>
          </div>

          {/* Disclosure Guidelines */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
              📋 CreatorGrid Disclosure Guidelines
            </h3>
            <p className="text-primary/80 mb-4">
              Given that Flexible Brand Deals via CreatorGrid are paid
              partnerships, it's important to disclose this appropriately in
              your content.
            </p>
            <ul className="list-disc list-inside space-y-2 text-primary/80 mb-4">
              <li>
                Use <strong>#ad</strong>, <strong>#sponsored</strong>, or{" "}
                <strong>
                  "Paid partnership with [Brand Name] via CreatorGrid"
                </strong>{" "}
                in your caption or video description.
              </li>
              <li>
                Make sure the disclosure is easy to see—not buried in hashtags
                or hidden at the end.
              </li>
              <li>
                For video, say or show that it's a paid partnership with the
                brand through CreatorGrid.
              </li>
            </ul>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                ⚠️ Disclosures are required by law in most countries, and help
                keep you, brands, and CreatorGrid protected.
              </p>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
              📚 FAQ
            </h3>

            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-primary mb-3">
                  🔸 CONTENT & POSTING
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-primary/90">
                      Q: Can I post the same video on multiple platforms?
                    </p>
                    <p className="text-primary/80">
                      Yes! In fact, we recommend it to boost your reach. Just
                      make sure all platforms are connected in CreatorGrid.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-primary/90">
                      Q: Can I post in a language other than English?
                    </p>
                    <p className="text-primary/80">
                      No, all videos must be in <strong>English</strong> for
                      this challenge.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-primary/90">
                      Q: Can I mention Voicepal in the handle or username?
                    </p>
                    <p className="text-primary/80">
                      No. Please don't use "Voicepal" in any account name.
                      Alternatives like{" "}
                      <code className="bg-secondary px-1 py-0.5 rounded text-xs">
                        @voicehacks
                      </code>{" "}
                      or{" "}
                      <code className="bg-secondary px-1 py-0.5 rounded text-xs">
                        @aivoicegenius
                      </code>{" "}
                      are fine.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-primary mb-3">
                  🔸 PAYMENTS & PERFORMANCE
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-primary/90">
                      Q: How do I earn money?
                    </p>
                    <p className="text-primary/80">
                      You'll earn based on{" "}
                      <strong>CPM (cost per thousand views)</strong>. We
                      automatically track your video views and update your
                      earnings in the dashboard. You'll be able to track them
                      there.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-primary/90">
                      Q: When and how do I get paid?
                    </p>
                    <p className="text-primary/80">
                      Payments will be sent after the challenge ends. We'll ask
                      you to upload your bank details (via our partner Stripe)
                      and pay you via bank transfer, directly through Stripe.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-primary mb-3">
                  🔸 ACCESS & SUPPORT
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-primary/90">
                      Q: What's the code to get free access to the app?
                    </p>
                    <p className="text-primary/80">
                      Use{" "}
                      <strong>
                        <code className="bg-secondary px-2 py-1 rounded text-sm">
                          GRID25
                        </code>
                      </strong>{" "}
                      after logging in to unlock unlimited Voicepal access for
                      the duration of the challenge.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-primary/90">
                      Q: Where do I ask questions or get help?
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-primary/80">
                      <li>
                        <a
                          href="https://calendly.com/melina-at-ali-abdaal/voicepal-creator-office-hours"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline font-medium"
                        >
                          Daily Office Hours
                        </a>
                      </li>
                      <li>Bug reports: Click your avatar → "Report a bug"</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-primary/90">
                      Q: Where can I find the CreatorGrid Terms and Conditions?
                    </p>
                    <p className="text-primary/80">
                      You can find them{" "}
                      <a
                        href="https://creatorgrid.io/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        here
                      </a>
                      . Please make sure you read through them to ensure all
                      your posts comply with guidelines.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Troubleshooting */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
              🛠 If You Get Stuck...
            </h3>
            <p className="text-primary/80">
              Click on your <strong>avatar {">"} Report a bug</strong> to flag
              technical issues
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
